import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { fetchServiceCatalog } from '../../../utils/serviceCatalog';
import { formatAUD } from '../../../utils/formatCurrency';
import {
  INVOICE_TYPE_OPTIONS,
  inferInvoiceType,
  calcInvoiceTotals,
  gstLineLabel,
  grandTotalLabel,
} from '../../../utils/invoiceTax';
import {
  useProfileBookingId,
  useProfileProjectId,
} from '../ProfileBookingContext';
import { displayTaskNotes } from '../../../utils/invoiceTaskUtils';
import QuotationServicePicker from './QuotationServicePicker';
import '../styles/EditInvoiceModal.css';
import '../styles/QuotationForm.css';

const CREATE_DRAFT_INVOICE = `
  mutation CreateDraftInvoiceForProject($bookingId: String!, $projectId: String) {
    createDraftInvoiceForProject(bookingId: $bookingId, projectId: $projectId) {
      id
      invoiceNumber
    }
  }
`;

const UPDATE_INVOICE = `
  mutation UpdateInvoice($invoiceId: String!, $rawData: JSON!) {
    updateInvoice(invoiceId: $invoiceId, rawData: $rawData) {
      id
      invoiceNumber
      invoiceStage
      rawData
    }
  }
`;

const FINALIZE_INVOICE = `
  mutation FinalizeDraftInvoice($invoiceId: String!) {
    finalizeDraftInvoice(invoiceId: $invoiceId) {
      id
      invoiceStage
    }
  }
`;

const GET_COMPANY_INVOICE_BANK = `
  query GetCompanyInvoiceBank {
    getMyProfile {
      company {
        invoiceBankName
        invoiceAccountName
        invoiceBsb
        invoiceAccountNumber
        invoiceBankReference
      }
    }
  }
`;

const FIND_INVOICE_CLIENT = `
  query FindInvoiceClient($id: String!) {
    findOneBooking(id: $id) {
      id
      fullName
      email
      phone
      address
      postcode
    }
  }
`;

const FIND_BOOKING_SERVICES = `
  query FindBookingService($bookingId: String!) {
    findBookingService(bookingId: $bookingId) {
      data {
        id
        price
        total
        frequency
        service {
          id
          service
          description
        }
      }
    }
  }
`;

const FIND_TASKS_BY_PROJECT = `
  query FindTasksByProject($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      remainder
      project {
        id
      }
    }
  }
`;

const FIND_PROJECT_SERVICES = `
  query FindOneProject($id: String!) {
    findOneProject(id: $id) {
      id
      seriesRootId
      startDate
      deadLine
      projectServices
      attention
      projectAddress
      deliveryAddress
      purchaseOrderNumber
    }
  }
`;

function newTaskRow(defaults = {}) {
  return {
    key: `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    taskId: defaults.taskId || '',
    title: defaults.title || '',
    groupName: defaults.groupName || '',
    scope: defaults.scope || '',
    // No internal work instructions: an invoice is a client document.
    description: defaults.description || '',
    // Catalog price, used when the service is switched to "Price by task".
    catalogPrice: Number(defaults.catalogPrice) || 0,
    notesFormat: defaults.notesFormat || '',
    price: Number(defaults.price) || 0,
    discount: Number(defaults.discount) || 0,
    total: Number(defaults.total) || 0,
    priceSource: defaults.priceSource || 'manual', // manual | service
  };
}

function calcLineTotal(price, discount) {
  const p = Number(price) || 0;
  const d = Number(discount) || 0;
  return Math.max(p * (1 - d / 100), 0);
}

/** Infer mode for older invoices that lacked pricingMode. */
function inferPricingMode(g) {
  if (g?.pricingMode === 'service' || g?.pricingMode === 'task') {
    return g.pricingMode;
  }
  const tasks = Array.isArray(g?.tasks) ? g.tasks : [];
  const prices = tasks
    .map((t) => Number(t.price) || 0)
    .filter((p) => p > 0);
  if (prices.length > 1 && prices.every((p) => p === prices[0])) {
    // Same price on every task → was the old buggy "copy service price" behaviour
    return 'service';
  }
  if (prices.length >= 1 && new Set(prices).size > 1) return 'task';
  if (Number(g?.servicePrice) > 0) return 'service';
  return 'service';
}

function newServiceGroup(defaults = {}) {
  const pricingMode = defaults.pricingMode || 'service';
  const servicePrice = Number(defaults.servicePrice) || 0;
  const serviceDiscount = Number(defaults.serviceDiscount) || 0;
  return {
    key: `svc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    serviceId: defaults.serviceId || '',
    serviceName: defaults.serviceName || '',
    frequency: defaults.frequency || 'One-time',
    pricingMode,
    servicePrice,
    serviceDiscount,
    serviceTotal:
      defaults.serviceTotal != null
        ? Number(defaults.serviceTotal) || 0
        : calcLineTotal(servicePrice, serviceDiscount),
    tasks: Array.isArray(defaults.tasks) && defaults.tasks.length
      ? defaults.tasks
      : [newTaskRow()],
  };
}

function groupHasSelectedTasks(group) {
  return (group?.tasks || []).some((t) => String(t?.taskId || '').trim());
}

function catalogServiceKeyFromTask(task) {
  const remainder = String(task?.remainder || '').trim();
  if (remainder.startsWith('svc:')) return remainder.slice(4);
  const desc = String(task?.description || '');
  const idLine = desc.match(/^Service:([0-9a-f-]{36})\s*$/im);
  if (idLine) return idLine[1];
  return '';
}

function groupsShareSameTaskIds(groups) {
  if (!Array.isArray(groups) || groups.length < 2) return false;
  const keys = groups.map((g) =>
    (g.tasks || [])
      .map((t) => String(t?.taskId || '').trim())
      .filter(Boolean)
      .sort()
      .join(','),
  );
  return keys.every((key) => key && key === keys[0]);
}

function toDateInputValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function serviceIdOfRow(row) {
  return String(row?.service?.id || row?.catalogServiceId || '').trim();
}

function serviceNameOfRow(row) {
  return String(
    row?.service?.service || row?.serviceName || row?.name || '',
  ).trim();
}

function snapshotTasksForService(projectServices, serviceId) {
  const rows = Array.isArray(projectServices) ? projectServices : [];
  const wanted = String(serviceId || '').trim();
  const row = rows.find((item) => serviceIdOfRow(item) === wanted);
  return (Array.isArray(row?.tasks) ? row.tasks : []).filter((task) =>
    String(task?.title || '').trim(),
  );
}

function operationalTasksForService(serviceId, serviceName, taskRows) {
  const list = Array.isArray(taskRows) ? taskRows : [];
  const wanted = String(serviceId || '').trim();
  const name = String(serviceName || '').trim().toLowerCase();
  return list.filter((task) => {
    const key = catalogServiceKeyFromTask(task);
    if (wanted && key === wanted) return true;
    if (key || !name) return false;
    const hay = `${task?.title || ''} ${task?.description || ''}`.toLowerCase();
    return hay.includes(name);
  });
}

function tasksAvailableForGroup(group, projectTasks) {
  const serviceId = String(group?.serviceId || '').trim();
  const list = Array.isArray(projectTasks) ? projectTasks : [];
  if (!serviceId) return list;
  const tagged = operationalTasksForService(
    serviceId,
    group?.serviceName,
    list,
  );
  return tagged.length ? tagged : list;
}

function labeledLine(description, label) {
  const match = String(description || '').match(
    new RegExp(`^${label}:\\s*(.+)$`, 'im'),
  );
  return String(match?.[1] || '').trim();
}

function taskFacing(task) {
  const description = String(task?.description || '');
  return {
    groupName: String(task?.groupName || labeledLine(description, 'Group')).trim(),
    scope: String(
      task?.scope || labeledLine(description, 'Client-facing scope'),
    ).trim(),
    instructions: String(
      task?.instructions ||
        labeledLine(description, 'Internal work instructions'),
    ).trim(),
  };
}

function taskTitleKey(task) {
  return String(task?.title || task?.taskTitle || '').trim().toLowerCase();
}

function selectedProjectTaskKeys(group, taskRows, projectServices, singleService) {
  const keys = new Set();
  snapshotTasksForService(projectServices, group?.serviceId).forEach((task) => {
    const key = taskTitleKey(task);
    if (key) keys.add(key);
  });
  const live = singleService
    ? taskRows
    : operationalTasksForService(group?.serviceId, group?.serviceName, taskRows);
  (live || []).forEach((task) => {
    const key = taskTitleKey(task);
    if (key) keys.add(key);
  });
  return keys;
}

function tasksOnThisProject(tasks, keys) {
  if (!keys.size) return tasks?.length ? tasks : [newTaskRow()];
  const kept = (tasks || []).filter((task) => {
    const key = taskTitleKey(task);
    if (!key) return false;
    if (keys.has(key)) return true;
    return (Number(task?.price) || Number(task?.total) || 0) > 0;
  });
  return kept.length ? kept : [newTaskRow()];
}

function mergeCatalogTasks(existing, catalogTasks) {
  if (!catalogTasks.length) return existing?.length ? existing : [newTaskRow()];
  const byTitle = new Map();
  (existing || []).forEach((task) => {
    const title = String(task?.title || '').trim().toLowerCase();
    if (title && !byTitle.has(title)) byTitle.set(title, task);
  });
  const used = new Set();
  const merged = catalogTasks.map((task) => {
    const title = String(task.title || '').trim().toLowerCase();
    const saved = title ? byTitle.get(title) : null;
    if (!saved) return task;
    used.add(title);
    return {
      ...task,
      ...saved,
      key: saved.key || task.key,
      title: saved.title || task.title,
      groupName: saved.groupName || task.groupName,
      scope: saved.scope || task.scope,
      catalogPrice: Number(saved.catalogPrice) || Number(task.catalogPrice) || 0,
    };
  });
  (existing || []).forEach((task) => {
    const title = String(task?.title || '').trim().toLowerCase();
    if (title && used.has(title)) return;
    if (!title && !task?.scope && !task?.taskId) return;
    merged.push(task);
  });
  return merged;
}

function invoiceBoardGroups(tasks, extras = []) {
  const map = new Map();
  (tasks || []).forEach((task, index) => {
    const name = String(task?.groupName || 'Tasks').trim() || 'Tasks';
    if (!map.has(name)) map.set(name, []);
    map.get(name).push({ task, index });
  });
  (extras || []).forEach((name) => {
    const label = String(name || '').trim();
    if (label && !map.has(label)) map.set(label, []);
  });
  if (!map.size) map.set('Tasks', []);
  return [...map.entries()].map(([name, rows]) => ({ name, rows }));
}

function invoiceTaskFrom(task, extra = {}) {
  const facing = taskFacing(task);
  return newTaskRow({
    taskId: task?.id || extra.taskId || '',
    title: task?.title || '',
    groupName: facing.groupName,
    scope: facing.scope,
    // Snapshot/catalog price, used when switching to "Price by task".
    catalogPrice: task?.price,
    description: displayTaskNotes(task?.description),
    notesFormat: task?.notesFormat || task?.taskNotesFormat || '',
    price: Number(extra.price) || 0,
    total: Number(extra.total) || Number(extra.price) || 0,
    priceSource: 'manual',
  });
}

function invoiceTaskRows(operational, snapshot) {
  const live = (Array.isArray(operational) ? operational : []).map((task) =>
    invoiceTaskFrom(task, { taskId: task.id }),
  );
  const planned = (Array.isArray(snapshot) ? snapshot : [])
    .filter((task) => String(task?.title || '').trim())
    .map((task) => invoiceTaskFrom(task));
  const rows = mergeCatalogTasks(live, planned);
  if (rows.some((row) => String(row.title || '').trim() || row.taskId)) return rows;
  return [newTaskRow()];
}

function tasksForServiceGroup(group, projectTasks, projectServices) {
  const serviceId = String(group?.serviceId || '').trim();
  const serviceName = String(group?.serviceName || '').trim();
  const operational = operationalTasksForService(
    serviceId,
    serviceName,
    projectTasks,
  );
  const snapshot = snapshotTasksForService(projectServices, serviceId);
  const rows = invoiceTaskRows(operational, snapshot);
  if (rows.some((row) => String(row.title || '').trim() || row.taskId)) {
    return rows;
  }
  return [newTaskRow()];
}

function groupsFromProject(projectServices, taskRows, priceMap) {
  const rows = Array.isArray(projectServices) ? projectServices : [];
  const used = new Set();
  const groups = [];
  rows.forEach((row) => {
    const serviceId = serviceIdOfRow(row);
    const serviceName = serviceNameOfRow(row) || 'Service';
    if (!serviceId && serviceName === 'Service' && !row?.tasks?.length) return;
    const operational = operationalTasksForService(
      serviceId,
      serviceName,
      taskRows,
    );
    operational.forEach((task) => {
      if (task?.id) used.add(task.id);
    });
    const price =
      Number(priceMap?.[serviceId]?.price) ||
      Number(row?.price) ||
      Number(row?.total) ||
      0;
    groups.push(
      newServiceGroup({
        serviceId,
        serviceName,
        frequency: row?.frequency || priceMap?.[serviceId]?.frequency || 'One-time',
        pricingMode: 'service',
        servicePrice: price,
        serviceTotal: price,
        tasks: invoiceTaskRows(operational, row?.tasks),
      }),
    );
  });
  const leftover = (taskRows || []).filter((task) => task?.id && !used.has(task.id));
  if (leftover.length && groups.length === 1) {
    const current = groups[0];
    const alreadyLive = (current.tasks || []).some((task) => task.taskId);
    groups[0] = {
      ...current,
      tasks: alreadyLive
        ? [
            ...(current.tasks || []).filter(
              (task) => String(task.title || '').trim() || task.taskId,
            ),
            ...invoiceTaskRows(leftover, []).filter(
              (task) =>
                !current.tasks.some((row) => row.taskId && row.taskId === task.taskId),
            ),
          ]
        : invoiceTaskRows(leftover, []),
    };
  } else if (leftover.length) {
    groups.push(
      newServiceGroup({
        serviceName: groups.length ? 'Other tasks' : 'Project tasks',
        tasks: invoiceTaskRows(leftover, []),
      }),
    );
  } else if (!groups.length && (taskRows || []).length) {
    groups.push(
      newServiceGroup({
        serviceName: 'Project tasks',
        tasks: invoiceTaskRows(taskRows, []),
      }),
    );
  }
  return groups;
}

function groupsAreBlank(groups) {
  return !(groups || []).some(
    (group) =>
      String(group?.serviceId || '').trim() ||
      Number(group?.servicePrice) > 0 ||
      (group?.tasks || []).some(
        (task) =>
          String(task?.taskId || '').trim() || String(task?.title || '').trim(),
      ),
  );
}

/** Project tasks as included lines (no per-task charge). */
function rowsFromProjectTasks(projectTasks) {
  const list = Array.isArray(projectTasks) ? projectTasks : [];
  if (!list.length) return [newTaskRow()];
  return list.map((task) => {
    const description = String(task?.description || '').trim();
    return newTaskRow({
      taskId: task.id || '',
      title: task.title || '',
      description,
      notesFormat: task.notesFormat || task.taskNotesFormat || '',
      price: 0,
      total: 0,
      priceSource: 'manual',
    });
  });
}

function groupBillableTotal(group) {
  if (group?.pricingMode === 'task') {
    return (group.tasks || []).reduce(
      (s, t) => s + (Number(t.total) || calcLineTotal(t.price, t.discount) || 0),
      0,
    );
  }
  return (
    Number(group?.serviceTotal) ||
    calcLineTotal(group?.servicePrice, group?.serviceDiscount) ||
    0
  );
}

function toJsonSafe(value, fallback = {}) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return fallback;
  }
}

function parseInvoiceRaw(invoice) {
  const source = invoice?.rawData ?? invoice ?? {};
  if (typeof source === 'string') {
    try {
      const parsed = JSON.parse(source);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return source && typeof source === 'object' ? source : {};
}

/** Load nested groups from rawData, or migrate flat services.data → groups. */
function groupsFromRaw(raw) {
  const groups = raw?.services?.groups;
  if (Array.isArray(groups) && groups.length) {
    return groups.map((g) => {
      const pricingMode = inferPricingMode(g);
      const tasksRaw = Array.isArray(g.tasks) ? g.tasks : [];
      const samePrice =
        tasksRaw.length > 1 &&
        tasksRaw.every(
          (t) =>
            (Number(t.price) || 0) > 0 &&
            (Number(t.price) || 0) === (Number(tasksRaw[0].price) || 0),
        );
      let servicePrice = Number(g.servicePrice);
      if (!servicePrice && pricingMode === 'service' && samePrice) {
        servicePrice = Number(tasksRaw[0].price) || 0;
      }
      const serviceDiscount = Number(g.serviceDiscount) || 0;
      return newServiceGroup({
        serviceId: g.serviceId || g.service?.id || '',
        serviceName: g.serviceName || g.service?.service || '',
        frequency: g.frequency || 'One-time',
        pricingMode,
        servicePrice,
        serviceDiscount,
        serviceTotal:
          g.serviceTotal != null
            ? g.serviceTotal
            : calcLineTotal(servicePrice, serviceDiscount),
        tasks: tasksRaw.map((t) =>
          newTaskRow({
            taskId: t.taskId || '',
            catalogPrice: t.catalogPrice || t.price,
            title: t.title || t.taskTitle || '',
            groupName: t.groupName || taskFacing(t).groupName,
            scope: t.scope || taskFacing(t).scope,
            description: t.description || t.taskDescription || '',
            notesFormat: t.notesFormat || t.taskNotesFormat || '',
            // In service mode, tasks are included — don't keep duplicated service price
            price: pricingMode === 'service' ? 0 : t.price,
            discount: pricingMode === 'service' ? 0 : t.discount,
            total: pricingMode === 'service' ? 0 : t.total ?? t.price,
            priceSource: t.priceSource || 'manual',
          }),
        ),
      });
    });
  }

  const data = raw?.services?.data;
  if (Array.isArray(data) && data.length) {
    const map = new Map();
    data.forEach((item) => {
      const sid = item?.service?.id || item?.serviceId || 'unknown';
      const sname = item?.service?.service || item?.serviceName || 'Service';
      if (!map.has(sid)) {
        map.set(
          sid,
          newServiceGroup({
            serviceId: sid === 'unknown' ? '' : sid,
            serviceName: sname,
            frequency: item.frequency || 'One-time',
            pricingMode: 'task',
            tasks: [],
          }),
        );
      }
      const group = map.get(sid);
      group.tasks.push(
        newTaskRow({
          taskId: item.taskId || '',
          title: item.taskTitle || '',
          description:
            item.taskDescription ||
            item.description ||
            item.service?.description ||
            '',
          notesFormat: item.notesFormat || item.taskNotesFormat || '',
          price: item.price,
          discount: item.discount,
          total: item.total,
          priceSource: item.priceSource || 'manual',
        }),
      );
    });
    return [...map.values()].map((g) => {
      const mode = inferPricingMode(g);
      if (mode === 'service') {
        const prices = (g.tasks || [])
          .map((t) => Number(t.price) || 0)
          .filter((p) => p > 0);
        const servicePrice =
          prices.length && prices.every((p) => p === prices[0])
            ? prices[0]
            : 0;
        return {
          ...g,
          pricingMode: 'service',
          servicePrice,
          serviceDiscount: 0,
          serviceTotal: servicePrice,
          tasks: (g.tasks || []).map((t) => ({
            ...t,
            price: 0,
            discount: 0,
            total: 0,
          })),
        };
      }
      return { ...g, pricingMode: 'task', tasks: g.tasks.length ? g.tasks : [newTaskRow()] };
    });
  }

  // No blank placeholder service: services are added from the catalog.
  return [];
}

function flattenGroups(groups) {
  const rows = [];
  groups.forEach((g) => {
    const mode = g.pricingMode === 'task' ? 'task' : 'service';
    if (mode === 'service') {
      const amount =
        Number(g.serviceTotal) ||
        calcLineTotal(g.servicePrice, g.serviceDiscount);
      rows.push({
        id: `${g.serviceId || 'svc'}-service`,
        frequency: g.frequency || 'One-time',
        discount: Number(g.serviceDiscount) || 0,
        price: Number(g.servicePrice) || 0,
        total: amount,
        taskId: null,
        taskTitle: null,
        taskDescription: null,
        description: g.serviceName || 'Service',
        priceSource: 'service',
        pricingMode: 'service',
        service: {
          id: g.serviceId || null,
          service: g.serviceName || 'Service',
          description: g.serviceName || 'Service',
        },
      });
      (g.tasks || []).forEach((t, idx) => {
        if (!t.taskId && !t.title) return;
        rows.push({
          id: `${g.serviceId || 'svc'}-inc-${t.taskId || idx}`,
          frequency: g.frequency || 'One-time',
          discount: 0,
          price: 0,
          total: 0,
          taskId: t.taskId || null,
          taskTitle: t.title || null,
          taskDescription: t.description || null,
          description: t.description || t.title || '',
          notesFormat: t.notesFormat || t.taskNotesFormat || '',
          priceSource: 'included',
          pricingMode: 'service',
          included: true,
          service: {
            id: g.serviceId || null,
            service: g.serviceName || 'Service',
            description: t.description || t.title || '',
          },
        });
      });
      return;
    }

    (g.tasks || []).forEach((t, idx) => {
      rows.push({
        id: `${g.serviceId || 'svc'}-${t.taskId || idx}`,
        frequency: g.frequency || 'One-time',
        discount: Number(t.discount) || 0,
        price: Number(t.price) || 0,
        total: Number(t.total) || calcLineTotal(t.price, t.discount),
        taskId: t.taskId || null,
        taskTitle: t.title || '',
        taskDescription: t.description || null,
        description: t.description || t.title || '',
        notesFormat: t.notesFormat || t.taskNotesFormat || '',
        priceSource: t.priceSource || 'manual',
        pricingMode: 'task',
        service: {
          id: g.serviceId || null,
          service: g.serviceName || 'Service',
          description: t.description || t.title || '',
        },
      });
    });
  });
  return rows;
}

export default function EditInvoiceModal({ invoice, projects = [], onClose, onSaved }) {
  const profileBookingId = useProfileBookingId();
  const profileProjectId = useProfileProjectId();
  const bookingId =
    invoice?.bookingId || invoice?.rawData?.bookingInfo?.id || profileBookingId;
  const lockedProjectId = invoice?.projectId || profileProjectId || '';
  const [chosenProjectId, setChosenProjectId] = useState(lockedProjectId);
  const projectId = chosenProjectId;

  const raw = useMemo(() => parseInvoiceRaw(invoice), [invoice]);
  const initialGroups = useMemo(() => groupsFromRaw(raw), [raw]);
  const initialBank = raw?.bankDetails || {};

  const [groups, setGroups] = useState(initialGroups);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [invoiceType, setInvoiceType] = useState(() => inferInvoiceType(raw));
  const [bankDetails, setBankDetails] = useState({
    bankName: initialBank.bankName || '',
    accountName: initialBank.accountName || '',
    bsb: initialBank.bsb || '',
    accountNumber: initialBank.accountNumber || initialBank.bankNumber || '',
    reference: initialBank.reference || '',
  });
  const [serviceDate, setServiceDate] = useState(() => {
    const rawDate = String(raw?.serviceDate || '').trim();
    if (!rawDate) return '';
    return rawDate.slice(0, 10);
  });
  const [serviceEndDate, setServiceEndDate] = useState(() => {
    const rawDate = String(raw?.serviceEndDate || '').trim();
    if (!rawDate) return '';
    return rawDate.slice(0, 10);
  });
  const [dueDate, setDueDate] = useState(() => {
    const stored = String(raw?.dueDate || '').trim();
    if (stored) return stored.slice(0, 10);
    const created = invoice?.createdAt;
    if (!created) return '';
    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return '';
    const plus = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 15);
    const y = plus.getFullYear();
    const m = String(plus.getMonth() + 1).padStart(2, '0');
    const day = String(plus.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const [services, setServices] = useState([]);
  const [bookingServicePrices, setBookingServicePrices] = useState({}); // serviceId → { price, frequency }
  const [tasks, setTasks] = useState([]);
  const [projectServiceSnapshot, setProjectServiceSnapshot] = useState([]);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [projectBilling, setProjectBilling] = useState({
    attention: String(raw?.attention || '').trim(),
    projectAddress: String(raw?.projectAddress || '').trim(),
    deliveryAddress: String(raw?.deliveryAddress || '').trim(),
    purchaseOrderNumber: String(raw?.purchaseOrderNumber || '').trim(),
  });
  const [clientDetails, setClientDetails] = useState(() => ({
    fullName: String(raw?.bookingInfo?.fullName || '').trim(),
    email: String(raw?.bookingInfo?.email || '').trim(),
    phone: String(raw?.bookingInfo?.phone || '').trim(),
    address: String(raw?.bookingInfo?.address || '').trim(),
  }));

  const lineSubtotal = groups.reduce(
    (sum, g) => sum + groupBillableTotal(g),
    0,
  );
  const gstIncluded = invoiceType === 'GST_INCLUDED';
  const { subtotalExGst, gst, grandTotal } = calcInvoiceTotals(
    lineSubtotal,
    invoiceType,
  );

  const stage = String(invoice?.invoiceStage || '').toUpperCase();
  const canFinalize = stage === 'DRAFT' && Boolean(invoice?.id);
  const alreadySent = stage === 'SENT';
  const readOnly = false;
  const isCustomInvoice =
    String(raw?.invoiceKind || raw?.services?.invoiceKind || '')
      .trim()
      .toUpperCase() === 'CUSTOM';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingLookups(true);
      try {
        const taskPromise = projectId
          ? graphqlRequest(FIND_TASKS_BY_PROJECT, { projectId }).catch(() => ({}))
          : Promise.resolve({});
        const projectPromise = projectId
          ? graphqlRequest(FIND_PROJECT_SERVICES, { id: projectId }).catch(
              () => ({}),
            )
          : Promise.resolve({});

        const [catalog, bookingSvcRes, taskRes, projectRes] = await Promise.all([
          fetchServiceCatalog().catch(() => []),
          bookingId
            ? graphqlRequest(FIND_BOOKING_SERVICES, { bookingId }).catch(() => ({}))
            : Promise.resolve({}),
          taskPromise,
          projectPromise,
        ]);
        if (cancelled) return;

        const companyServices = Array.isArray(catalog) ? catalog : [];
        const bookingRows = bookingSvcRes?.findBookingService?.data || [];
        const priceMap = {};
        bookingRows.forEach((row) => {
          const sid = row?.service?.id;
          if (!sid) return;
          priceMap[sid] = {
            price: Number(row.total ?? row.price) || 0,
            frequency: row.frequency || 'One-time',
          };
        });
        setBookingServicePrices(priceMap);

        const byId = new Map();
        [
          ...companyServices,
          ...bookingRows.map((r) => r?.service).filter((s) => s?.id),
        ].forEach((s) => {
          if (s?.id) byId.set(s.id, s);
        });

        let taskRows = Array.isArray(taskRes?.findTasksByProject)
          ? taskRes.findTasksByProject
          : [];
        if (projectId) {
          const scoped = taskRows.filter((t) => {
            const pid = t?.project?.id;
            return !pid || String(pid) === String(projectId);
          });
          if (scoped.length) taskRows = scoped;
        }
        setTasks(taskRows);
        let projectRow = projectRes?.findOneProject || {};
        const ownServices = Array.isArray(projectRow.projectServices)
          ? projectRow.projectServices
          : [];
        let rootServices = [];
        const rootId = String(projectRow.seriesRootId || '').trim();
        const needsRoot =
          rootId &&
          (!String(projectRow.purchaseOrderNumber || '').trim() ||
            !projectRow.startDate ||
            !projectRow.deadLine);
        if (needsRoot) {
          const rootRes = await graphqlRequest(FIND_PROJECT_SERVICES, {
            id: rootId,
          }).catch(() => ({}));
          const root = rootRes?.findOneProject || {};
          rootServices = Array.isArray(root.projectServices)
            ? root.projectServices
            : [];
          projectRow = {
            ...root,
            ...projectRow,
            startDate: projectRow.startDate || root.startDate,
            deadLine: projectRow.deadLine || root.deadLine,
            attention: projectRow.attention || root.attention,
            projectAddress: projectRow.projectAddress || root.projectAddress,
            deliveryAddress: projectRow.deliveryAddress || root.deliveryAddress,
            purchaseOrderNumber:
              projectRow.purchaseOrderNumber || root.purchaseOrderNumber,
          };
        }
        const projectServices = ownServices.length ? ownServices : rootServices;
        projectServices.forEach((row) => {
          const id = serviceIdOfRow(row);
          const name = serviceNameOfRow(row);
          if (id && !byId.has(id)) byId.set(id, { id, service: name || 'Service' });
        });
        setServices([...byId.values()]);
        setProjectServiceSnapshot(projectServices);
        const billingFromProject = {
          attention: String(projectRow.attention || '').trim(),
          projectAddress: String(projectRow.projectAddress || '').trim(),
          deliveryAddress: String(projectRow.deliveryAddress || '').trim(),
          purchaseOrderNumber: String(projectRow.purchaseOrderNumber || '').trim(),
        };
        if (!invoice?.id) {
          setProjectBilling(billingFromProject);
        } else {
          setProjectBilling({
            attention:
              String(raw?.attention || '').trim() || billingFromProject.attention,
            projectAddress:
              String(raw?.projectAddress || '').trim() ||
              billingFromProject.projectAddress,
            deliveryAddress:
              String(raw?.deliveryAddress || '').trim() ||
              billingFromProject.deliveryAddress,
            purchaseOrderNumber:
              String(raw?.purchaseOrderNumber || '').trim() ||
              billingFromProject.purchaseOrderNumber,
          });
        }
        if (bookingId) {
          const clientRes = await graphqlRequest(FIND_INVOICE_CLIENT, {
            id: bookingId,
          }).catch(() => ({}));
          const booking = clientRes?.findOneBooking || {};
          const clientAddress = [booking.address, booking.postcode]
            .map((part) => String(part || '').trim())
            .filter(Boolean)
            .join(', ');
          const pickClient = (saved, fetched) => {
            const kept = String(saved || '').trim();
            if (invoice?.id && kept) return kept;
            return String(fetched || '').trim() || kept;
          };
          if (!cancelled) {
            setClientDetails({
              fullName: pickClient(raw?.bookingInfo?.fullName, booking.fullName),
              email: pickClient(raw?.bookingInfo?.email, booking.email),
              phone: pickClient(raw?.bookingInfo?.phone, booking.phone),
              address: pickClient(raw?.bookingInfo?.address, clientAddress),
            });
          }
        }
        const projectStart = toDateInputValue(projectRow.startDate);
        const projectEnd = toDateInputValue(projectRow.deadLine);
        if (!invoice?.id) {
          setServiceDate(projectStart);
          setServiceEndDate(projectEnd);
        } else {
          if (!String(raw?.serviceDate || '').trim() && projectStart) {
            setServiceDate(projectStart);
          }
          if (!String(raw?.serviceEndDate || '').trim() && projectEnd) {
            setServiceEndDate(projectEnd);
          }
        }

        // Prefill bank details from Settings → Payments when invoice has none yet.
        const bankEmpty =
          !String(initialBank.bankName || '').trim() &&
          !String(initialBank.accountName || '').trim() &&
          !String(initialBank.bsb || '').trim() &&
          !String(initialBank.accountNumber || initialBank.bankNumber || '').trim() &&
          !String(initialBank.reference || '').trim();
        if (bankEmpty) {
          try {
            const bankRes = await graphqlRequest(GET_COMPANY_INVOICE_BANK);
            const c = bankRes?.getMyProfile?.company;
            if (c && !cancelled) {
              setBankDetails({
                bankName: String(c.invoiceBankName || '').trim(),
                accountName: String(c.invoiceAccountName || '').trim(),
                bsb: String(c.invoiceBsb || '').trim(),
                accountNumber: String(c.invoiceAccountNumber || '').trim(),
                reference: String(c.invoiceBankReference || '').trim(),
              });
            }
          } catch {
            // Non-fatal — admin can still type bank details manually.
          }
        }

        // Custom invoices are a separate extra-job document — do not seed
        // regular project services/tasks onto them when editing.
        if (isCustomInvoice) {
          return;
        }

        const serviceLines = (Array.isArray(projectServices) ? projectServices : []).filter(
          (row) =>
            serviceIdOfRow(row) ||
            serviceNameOfRow(row) ||
            (Array.isArray(row?.tasks) && row.tasks.length),
        );
        const singleService = serviceLines.length <= 1;
        const withServiceTasks = (group, tasks) =>
          tasksOnThisProject(
            tasks,
            selectedProjectTaskKeys(
              group,
              taskRows,
              projectServices,
              singleService,
            ),
          );
        const seededGroups = groupsFromProject(
          projectServices,
          taskRows,
          priceMap,
        ).map((group) => ({
          ...group,
          tasks: withServiceTasks(group, group.tasks),
        }));
        setGroups((prev) => {
          // A new invoice is filled from the selected project's services and
          // tasks (no invoice is auto-created any more); + Add service adds more.
          if (!invoice?.id) return seededGroups.length ? seededGroups : prev;
          if (groupsAreBlank(prev) && seededGroups.length) {
            return seededGroups;
          }
          const identicalDump = groupsShareSameTaskIds(prev);
          return prev.map((g) => {
            const savedPrice =
              Number(g.servicePrice) || Number(g.serviceTotal) || 0;
            const hasSavedTasks = (g.tasks || []).some(
              (t) =>
                String(t?.taskId || '').trim() ||
                String(t?.title || '').trim() ||
                Number(t?.price) > 0,
            );
            const keepOwn = hasSavedTasks && !identicalDump;
            const snapshot = snapshotTasksForService(
              projectServices,
              g.serviceId,
            );
            const seeded =
              !keepOwn && snapshot.length
                ? {
                    ...g,
                    tasks: snapshot.map((task) => {
                      const title = String(task.title || '').trim().toLowerCase();
                      const match = taskRows.find((row) => {
                        const sameService =
                          catalogServiceKeyFromTask(row) ===
                          String(g.serviceId || '');
                        return (
                          sameService &&
                          String(row.title || '').trim().toLowerCase() === title
                        );
                      });
                      return newTaskRow({
                        taskId: match?.id || '',
                        title: task.title,
                        description: task.description,
                        notesFormat:
                          task.notesFormat || task.taskNotesFormat || '',
                        price: Number(task.price) || 0,
                        total: Number(task.total) || Number(task.price) || 0,
                      });
                    }),
                  }
                : g;
            const nextTasks = keepOwn
              ? g.tasks
              : tasksForServiceGroup(seeded, taskRows, projectServices);
            const fallbackPrice = Number(priceMap[g.serviceId]?.price) || 0;
            const nextPrice = savedPrice > 0 ? savedPrice : fallbackPrice;
            const freq =
              g.frequency ||
              (g.serviceId && priceMap[g.serviceId]?.frequency) ||
              'One-time';
            return {
              ...g,
              frequency: freq,
              servicePrice: nextPrice,
              serviceDiscount: Number(g.serviceDiscount) || 0,
              serviceTotal:
                Number(g.serviceTotal) > 0
                  ? Number(g.serviceTotal)
                  : calcLineTotal(nextPrice, Number(g.serviceDiscount) || 0),
              pricingMode: g.pricingMode || 'service',
              tasks: withServiceTasks(g, nextTasks),
            };
          });
        });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [bookingId, projectId, isCustomInvoice, initialBank.bankName, initialBank.accountName, initialBank.bsb, initialBank.accountNumber, initialBank.bankNumber, initialBank.reference]);

  const serviceDefaultPrice = (serviceId) =>
    Number(bookingServicePrices[serviceId]?.price) || 0;

  const updateGroup = (gIndex, patch) => {
    setGroups((prev) =>
      prev.map((g, i) => (i === gIndex ? { ...g, ...patch } : g)),
    );
  };

  const updateTask = (gIndex, tIndex, patch) => {
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== gIndex) return g;
        const tasksNext = (g.tasks || []).map((t, j) => {
          if (j !== tIndex) return t;
          const next = { ...t, ...patch };
          next.total = calcLineTotal(next.price, next.discount);
          return next;
        });
        return { ...g, tasks: tasksNext };
      }),
    );
  };

  const onSelectService = (gIndex, serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    const fallback = serviceDefaultPrice(serviceId);
    const freq = bookingServicePrices[serviceId]?.frequency || 'One-time';
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== gIndex) return g;
        const mode = g.pricingMode || 'service';
        const keepServicePrice =
          mode === 'service' && Number(g.servicePrice) > 0 && g.serviceId === serviceId;
        const servicePrice = keepServicePrice ? Number(g.servicePrice) : fallback;
        const base = {
          ...g,
          serviceId: serviceId || '',
          serviceName: svc?.service || '',
          frequency: freq,
          servicePrice,
          serviceTotal: calcLineTotal(servicePrice, g.serviceDiscount),
        };

        const projectRows = tasksForServiceGroup(base, tasks, projectServiceSnapshot);
        return {
          ...base,
          activeGroup:
            projectRows.find((task) => task.groupName)?.groupName ||
            base.activeGroup ||
            '',
          tasks: projectRows,
        };
      }),
    );
  };

  const setPricingMode = (gIndex, mode) => {
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== gIndex) return g;
        if (mode === 'service') {
          const fallback = serviceDefaultPrice(g.serviceId);
          const servicePrice =
            Number(g.servicePrice) > 0 ? Number(g.servicePrice) : fallback;
          return {
            ...g,
            pricingMode: 'service',
            servicePrice,
            serviceTotal: calcLineTotal(servicePrice, g.serviceDiscount),
            tasks: (g.tasks || []).map((t) => ({
              ...t,
              price: 0,
              discount: 0,
              total: 0,
              priceSource: 'manual',
            })),
          };
        }
        return {
          ...g,
          pricingMode: 'task',
          tasks: (g.tasks || []).map((t) => {
            // Fall back to the task's catalog price; admin can still change it.
            const price = Number(t.price) || Number(t.catalogPrice) || 0;
            return { ...t, price, total: calcLineTotal(price, t.discount) };
          }),
        };
      }),
    );
  };

  /** Service picked from the catalog (tier → task groups → tasks), as an invoice line. */
  const addCatalogService = (pick) => {
    const byTask = Boolean(pick.pricedByTasks);
    const amount = Number(pick.unitPrice) || 0;
    const group = newServiceGroup({
      serviceId: pick.catalogId,
      serviceName: pick.name,
      pricingMode: byTask ? 'task' : 'service',
      servicePrice: byTask ? 0 : amount,
      serviceTotal: byTask ? 0 : amount,
      tasks: (pick.tasks || []).map((t) =>
        newTaskRow({
          title: t.title,
          groupName: t.groupName,
          scope: t.scope,
          description: t.scope,
          catalogPrice: t.price,
          price: byTask ? t.price : 0,
          total: byTask ? t.price : 0,
        }),
      ),
    });
    setGroups((prev) => (groupsAreBlank(prev) ? [group] : [...prev, group]));
    setShowCatalogPicker(false);
  };

  const removeServiceGroup = (gIndex) => {
    setGroups((prev) => prev.filter((_, i) => i !== gIndex));
  };

  const addTask = (gIndex) => {
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== gIndex) return g;
        const board = invoiceBoardGroups(g.tasks, g.extraGroups);
        const activeName = board.some((row) => row.name === g.activeGroup)
          ? g.activeGroup
          : board[0]?.name || 'Tasks';
        return {
          ...g,
          activeGroup: activeName,
          tasks: [...(g.tasks || []), newTaskRow({ groupName: activeName })],
        };
      }),
    );
  };

  const removeTask = (gIndex, tIndex) => {
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== gIndex) return g;
        const next = (g.tasks || []).filter((_, j) => j !== tIndex);
        return { ...g, tasks: next.length ? next : [newTaskRow()] };
      }),
    );
  };

  const buildRawData = () => {
    const bookingInfo = {
      ...(raw.bookingInfo || {}),
      id: invoice?.bookingId || raw.bookingInfo?.id || bookingId,
      fullName: clientDetails.fullName.trim() || raw.bookingInfo?.fullName || '',
      email: clientDetails.email.trim() || raw.bookingInfo?.email || '',
      phone: clientDetails.phone.trim() || raw.bookingInfo?.phone || '',
      address: clientDetails.address.trim() || raw.bookingInfo?.address || '',
    };
    const nestedGroups = groups.map((g) => ({
      serviceId: g.serviceId || null,
      serviceName: g.serviceName || 'Service',
      frequency: g.frequency || 'One-time',
      pricingMode: g.pricingMode === 'task' ? 'task' : 'service',
      servicePrice: Number(g.servicePrice) || 0,
      serviceDiscount: Number(g.serviceDiscount) || 0,
      serviceTotal:
        Number(g.serviceTotal) ||
        calcLineTotal(g.servicePrice, g.serviceDiscount),
      service: {
        id: g.serviceId || null,
        service: g.serviceName || 'Service',
      },
      tasks: (g.tasks || []).map((t) => ({
        taskId: t.taskId || null,
        catalogPrice: Number(t.catalogPrice) || 0,
        title: t.title || '',
        groupName: String(t.groupName || '').trim(),
        scope: t.scope || '',
        description: t.description || t.title || '',
        notesFormat: t.notesFormat || t.taskNotesFormat || '',
        price: g.pricingMode === 'service' ? 0 : Number(t.price) || 0,
        discount: g.pricingMode === 'service' ? 0 : Number(t.discount) || 0,
        total:
          g.pricingMode === 'service'
            ? 0
            : Number(t.total) || calcLineTotal(t.price, t.discount),
        priceSource: t.priceSource || 'manual',
      })),
    }));
    const flat = flattenGroups(groups);

    const customKind =
      String(raw.invoiceKind || raw.services?.invoiceKind || '')
        .trim()
        .toUpperCase() === 'CUSTOM';
    const payload = {
      companyInfo: toJsonSafe(raw.companyInfo || {}),
      bookingInfo: toJsonSafe(bookingInfo),
      invoiceType,
      gstIncluded,
      serviceDate: serviceDate || null,
      serviceEndDate: serviceEndDate || null,
      dueDate: dueDate || raw.dueDate || null,
      invoiceKind: customKind ? 'CUSTOM' : raw.invoiceKind || null,
      customTitle: raw.customTitle || null,
      clientDescription: raw.clientDescription || raw.customTitle || null,
      bankDetails: {
        bankName: bankDetails.bankName.trim(),
        accountName: bankDetails.accountName.trim(),
        bsb: bankDetails.bsb.trim(),
        accountNumber: bankDetails.accountNumber.trim(),
        reference: bankDetails.reference.trim(),
      },
      services: {
        groups: nestedGroups,
        data: flat,
        invoiceType,
        gstIncluded,
        invoiceKind: customKind ? 'CUSTOM' : raw.services?.invoiceKind || null,
        totalPrice: gstIncluded ? subtotalExGst : lineSubtotal,
        totalWithTax: grandTotal,
      },
      totalAmount: grandTotal,
      amountDue: grandTotal,
      paymentStatus: invoice?.paymentStatus || raw.paymentStatus || 'unpaid',
      invoiceNumber: invoice?.invoiceNumber || raw.invoiceNumber,
      projectId: invoice?.projectId || projectId || raw.projectId || null,
      attention: projectBilling.attention.trim() || null,
      projectAddress: projectBilling.projectAddress.trim() || null,
      deliveryAddress: projectBilling.deliveryAddress.trim() || null,
      purchaseOrderNumber: projectBilling.purchaseOrderNumber.trim() || null,
    };
    return toJsonSafe(payload);
  };

  const handleSave = async () => {
    if (!invoice?.id && !projectId) {
      toast.error('Select the project for this invoice.');
      return;
    }
    if (!groups.length) {
      toast.error('Add a service to this invoice.');
      return;
    }
    try {
      setSaving(true);
      let invoiceId = invoice?.id ? String(invoice.id) : '';
      let createdNumber = '';
      if (!invoiceId) {
        const created = await graphqlRequest(CREATE_DRAFT_INVOICE, {
          bookingId,
          projectId,
        });
        invoiceId = created?.createDraftInvoiceForProject?.id || '';
        createdNumber = created?.createDraftInvoiceForProject?.invoiceNumber || '';
        if (!invoiceId) {
          toast.error('Could not create draft invoice');
          return;
        }
      }
      await graphqlRequest(UPDATE_INVOICE, {
        invoiceId,
        rawData: buildRawData(),
      });
      toast.success(
        alreadySent
          ? 'Invoice updated. Resend the email so the client gets the new GST and totals.'
          : createdNumber
            ? `Regular invoice ${createdNumber} saved`
            : 'Invoice updated',
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      await graphqlRequest(UPDATE_INVOICE, {
        invoiceId: invoice.id,
        rawData: buildRawData(),
      });
      await graphqlRequest(FINALIZE_INVOICE, { invoiceId: invoice.id });
      toast.success('Invoice finalized');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to finalize invoice');
    } finally {
      setFinalizing(false);
    }
  };

  const fieldLabel = (text) => <span className="label">{text}</span>;

  return createPortal(
    <div
      className="eim-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      <div
        className="eim-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Edit invoice"
      >
        <div className="eim-header">
          <div>
            <h2>{invoice?.id ? 'Edit invoice' : 'Create regular invoice'}</h2>
            <div className="eim-header-meta">
              <span className="eim-chip">
                {invoice?.invoiceNumber || 'Draft'}
              </span>
              {stage && invoice?.invoiceNumber ? (
                <span className="eim-chip">{stage}</span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="eim-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="eim-body">
          {loadingLookups && (
            <p className="eim-muted">Loading services & tasks…</p>
          )}

          {alreadySent ? (
            <p className="eim-hint eim-hint--warn">
              This invoice was already emailed. You can still change GST or
              lines, save, then resend the invoice from the list.
            </p>
          ) : (
            <p className="eim-hint">
              Choose the invoice type, then set service prices. Default is a
              standard tax invoice (GST added). Switch to GST included if line
              prices already contain tax.
            </p>
          )}

          <div className="eim-options">
            {!invoice?.id && !lockedProjectId && projects.length ? (
              <div className="eim-field">
                {fieldLabel('Project')}
                <select
                  value={chosenProjectId}
                  onChange={(e) => setChosenProjectId(e.target.value)}
                >
                  <option value="">Select project</option>
                  {projects.map((row) => (
                    <option key={row.id} value={row.id}>
                      {[row.projectId, row.name].filter(Boolean).join(' — ') || 'Project'}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="eim-field">
              {fieldLabel('Invoice type')}
              <select
                disabled={readOnly}
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value)}
              >
                {INVOICE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <small>
                {INVOICE_TYPE_OPTIONS.find((o) => o.value === invoiceType)
                  ?.hint}
              </small>
            </div>
          </div>

          {!readOnly && (
            <button
              type="button"
              className="eim-btn eim-btn--ghost"
              onClick={() => setShowCatalogPicker(true)}
            >
              + Add service
            </button>
          )}
          {!groups.length ? (
            <p className="eim-section-label">
              No services yet. Use + Add service to pick one from the catalog.
            </p>
          ) : null}

          {groups.map((group, gIndex) => {
            const byTask = group.pricingMode === 'task';
            return (
            <div key={group.key} className="eim-service-card">
              <div className="eim-service-top">
                <div className="eim-field">
                  {fieldLabel('Service')}
                  <select
                    disabled={readOnly}
                    value={group.serviceId || ''}
                    onChange={(e) => onSelectService(gIndex, e.target.value)}
                  >
                    <option value="">Select service</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.service}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="eim-field">
                  {fieldLabel('Frequency')}
                  <input
                    disabled={readOnly}
                    value={group.frequency}
                    onChange={(e) =>
                      updateGroup(gIndex, { frequency: e.target.value })
                    }
                  />
                </div>
                <button
                  type="button"
                  className="eim-btn eim-btn--danger"
                  disabled={readOnly}
                  onClick={() => removeServiceGroup(gIndex)}
                >
                  Remove
                </button>
              </div>

              <div className="eim-pricing-mode" role="group" aria-label="Pricing mode">
                <button
                  type="button"
                  disabled={readOnly}
                  className={`eim-mode-btn${!byTask ? ' is-active' : ''}`}
                  onClick={() => setPricingMode(gIndex, 'service')}
                >
                  Price by service
                </button>
                <button
                  type="button"
                  disabled={readOnly}
                  className={`eim-mode-btn${byTask ? ' is-active' : ''}`}
                  onClick={() => setPricingMode(gIndex, 'task')}
                >
                  Price by task
                </button>
              </div>

              {!byTask && (
                <div className="eim-service-price-row">
                  <div className="eim-field">
                    {fieldLabel('Service price')}
                    <input
                      disabled={readOnly}
                      type="number"
                      min="0"
                      step="0.01"
                      value={group.servicePrice}
                      onChange={(e) => {
                        const servicePrice = e.target.value;
                        updateGroup(gIndex, {
                          servicePrice,
                          serviceTotal: calcLineTotal(
                            servicePrice,
                            group.serviceDiscount,
                          ),
                        });
                      }}
                      placeholder={
                        group.serviceId && serviceDefaultPrice(group.serviceId)
                          ? `Booking ${serviceDefaultPrice(group.serviceId)}`
                          : '0'
                      }
                    />
                  </div>
                  <div className="eim-field">
                    {fieldLabel('Disc %')}
                    <input
                      disabled={readOnly}
                      type="number"
                      min="0"
                      max="100"
                      value={group.serviceDiscount}
                      onChange={(e) => {
                        const serviceDiscount = e.target.value;
                        updateGroup(gIndex, {
                          serviceDiscount,
                          serviceTotal: calcLineTotal(
                            group.servicePrice,
                            serviceDiscount,
                          ),
                        });
                      }}
                    />
                  </div>
                  <div className="eim-field">
                    {fieldLabel('Amount')}
                    <input
                      disabled
                      value={Number(
                        group.serviceTotal ||
                          calcLineTotal(group.servicePrice, group.serviceDiscount),
                      ).toFixed(2)}
                    />
                  </div>
                </div>
              )}

              <div className="eim-section-label">
                {byTask
                  ? 'Tasks for this service (priced individually)'
                  : 'Tasks included in this service'}
              </div>
              <p className="eim-scope-note">
                Groups and client-facing scope are copied onto this invoice. Editing them does not change Service Management.
              </p>

              {(() => {
                const board = invoiceBoardGroups(group.tasks, group.extraGroups);
                const activeName = board.some((row) => row.name === group.activeGroup)
                  ? group.activeGroup
                  : board[0]?.name || 'Tasks';
                const activeTasks = board.find((row) => row.name === activeName)?.rows || [];
                return (
                  <div className="eim-scope-board">
                    <aside>
                      <header>
                        <span>Task groups</span>
                        <strong>{board.length}</strong>
                      </header>
                      {board.map((row) => (
                        <button
                          key={row.name}
                          type="button"
                          className={row.name === activeName ? 'is-on' : ''}
                          onClick={() => updateGroup(gIndex, { activeGroup: row.name })}
                        >
                          <b>{row.name}</b>
                          <small>{row.rows.length} tasks</small>
                        </button>
                      ))}
                      {!readOnly ? (
                        <div className="eim-scope-new">
                          <input
                            value={group.newGroupName || ''}
                            placeholder="New group"
                            onChange={(e) =>
                              updateGroup(gIndex, { newGroupName: e.target.value })
                            }
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const name = String(group.newGroupName || '').trim();
                              if (!name) return;
                              updateGroup(gIndex, {
                                newGroupName: '',
                                activeGroup: name,
                                extraGroups: [
                                  ...new Set([...(group.extraGroups || []), name]),
                                ],
                              });
                            }}
                          >
                            Add group
                          </button>
                        </div>
                      ) : null}
                    </aside>
                    <section>
                      <header>
                        <div>
                          <h4>{activeName}</h4>
                          <span>{activeTasks.length} tasks</span>
                        </div>
                        {!readOnly ? (
                          <button type="button" onClick={() => addTask(gIndex)}>
                            ＋ Add task
                          </button>
                        ) : null}
                      </header>
                      {activeTasks.map(({ task, index }) => (
                        <article className="eim-task-card" key={task.key}>
                          <header>
                            <span>{task.title || 'Task'}</span>
                            <button
                              type="button"
                              disabled={readOnly || (group.tasks || []).length <= 1}
                              onClick={() => removeTask(gIndex, index)}
                            >
                              Remove
                            </button>
                          </header>
                          <label>
                            Task name
                            <input
                              disabled={readOnly}
                              value={task.title || ''}
                              placeholder="Task name"
                              onChange={(e) =>
                                updateTask(gIndex, index, { title: e.target.value })
                              }
                            />
                          </label>
                          <label>
                            Client-facing scope
                            <textarea
                              disabled={readOnly}
                              rows={3}
                              value={task.scope || ''}
                              onChange={(e) =>
                                updateTask(gIndex, index, { scope: e.target.value })
                              }
                            />
                          </label>
                          {byTask ? (
                            <div className="eim-task-price">
                              <label>
                                Price
                                <input
                                  disabled={readOnly}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={task.price}
                                  onChange={(e) =>
                                    updateTask(gIndex, index, {
                                      price: e.target.value,
                                      priceSource: 'manual',
                                    })
                                  }
                                />
                              </label>
                              <label>
                                Disc %
                                <input
                                  disabled={readOnly}
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={task.discount}
                                  onChange={(e) =>
                                    updateTask(gIndex, index, {
                                      discount: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label>
                                Amount
                                <input
                                  disabled
                                  value={Number(task.total || 0).toFixed(2)}
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="eim-included-tag">Included</div>
                          )}
                        </article>
                      ))}
                      {!activeTasks.length ? (
                        <p className="eim-scope-note">No tasks in this group yet.</p>
                      ) : null}
                    </section>
                  </div>
                );
              })()}
            </div>
            );
          })}

          {showCatalogPicker
            ? createPortal(
                <QuotationServicePicker
                  bookingId={bookingId}
                  persist={false}
                  zIndex={20010}
                  onClose={() => setShowCatalogPicker(false)}
                  onAdded={addCatalogService}
                />,
                document.body,
              )
            : null}

          <div className="eim-bank">
            <h3>Client details</h3>
            <div className="eim-bank-grid">
              <div className="eim-field">
                {fieldLabel('Name')}
                <input
                  disabled={readOnly}
                  value={clientDetails.fullName}
                  onChange={(e) =>
                    setClientDetails((prev) => ({
                      ...prev,
                      fullName: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Phone')}
                <input
                  disabled={readOnly}
                  value={clientDetails.phone}
                  onChange={(e) =>
                    setClientDetails((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Email')}
                <input
                  disabled={readOnly}
                  value={clientDetails.email}
                  onChange={(e) =>
                    setClientDetails((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Address')}
                <input
                  disabled={readOnly}
                  value={clientDetails.address}
                  onChange={(e) =>
                    setClientDetails((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Attention')}
                <input
                  disabled={readOnly}
                  value={projectBilling.attention}
                  onChange={(e) =>
                    setProjectBilling((prev) => ({
                      ...prev,
                      attention: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Project address')}
                <input
                  disabled={readOnly}
                  value={projectBilling.projectAddress}
                  onChange={(e) =>
                    setProjectBilling((prev) => ({
                      ...prev,
                      projectAddress: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Delivery address')}
                <input
                  disabled={readOnly}
                  value={projectBilling.deliveryAddress}
                  onChange={(e) =>
                    setProjectBilling((prev) => ({
                      ...prev,
                      deliveryAddress: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('PO number')}
                <input
                  disabled={readOnly}
                  value={projectBilling.purchaseOrderNumber}
                  onChange={(e) =>
                    setProjectBilling((prev) => ({
                      ...prev,
                      purchaseOrderNumber: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="eim-bank">
            <h3>Service dates</h3>
            <div className="eim-bank-grid">
              <div className="eim-field">
                {fieldLabel('Service date from')}
                <input
                  type="date"
                  disabled={readOnly}
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value)}
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Service date to')}
                <input
                  type="date"
                  disabled={readOnly}
                  value={serviceEndDate}
                  onChange={(e) => setServiceEndDate(e.target.value)}
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Payment due date')}
                <input
                  type="date"
                  disabled={readOnly}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="eim-bank">
            <h3>Bank details</h3>
            <div className="eim-bank-grid">
              <div className="eim-field">
                {fieldLabel('Bank name')}
                <input
                  disabled={readOnly}
                  value={bankDetails.bankName}
                  onChange={(e) =>
                    setBankDetails((p) => ({ ...p, bankName: e.target.value }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Account name')}
                <input
                  disabled={readOnly}
                  value={bankDetails.accountName}
                  onChange={(e) =>
                    setBankDetails((p) => ({ ...p, accountName: e.target.value }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('BSB')}
                <input
                  disabled={readOnly}
                  value={bankDetails.bsb}
                  onChange={(e) =>
                    setBankDetails((p) => ({ ...p, bsb: e.target.value }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Account / bank number')}
                <input
                  disabled={readOnly}
                  value={bankDetails.accountNumber}
                  onChange={(e) =>
                    setBankDetails((p) => ({
                      ...p,
                      accountNumber: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="eim-field">
                {fieldLabel('Reference')}
                <input
                  disabled={readOnly}
                  value={bankDetails.reference}
                  onChange={(e) =>
                    setBankDetails((p) => ({ ...p, reference: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="eim-totals-bar">
            <div className="eim-type-summary">
              <span
                className={`eim-gst-badge eim-gst-badge--${invoiceType
                  .toLowerCase()
                  .replace(/_/g, '-')}`}
              >
                {invoiceType === 'GST_INCLUDED'
                  ? 'GST included'
                  : invoiceType === 'GST_EXCLUDED'
                    ? 'GST excluded'
                    : 'Standard invoice'}
              </span>
              <small>
                {invoiceType === 'STANDARD'
                  ? 'GST 10% is added to the subtotal.'
                  : invoiceType === 'GST_INCLUDED'
                    ? 'Prices include GST. GST is shown as included.'
                    : 'No GST is added to this invoice.'}
              </small>
            </div>
            <div className="eim-totals">
              {invoiceType === 'GST_INCLUDED' ? (
                <>
                  <div>
                    Total (GST included):{' '}
                    <strong>{formatAUD(grandTotal)}</strong>
                  </div>
                  <div>
                    {gstLineLabel(invoiceType)}:{' '}
                    <strong>{formatAUD(gst)}</strong>
                  </div>
                  <div>
                    Ex GST: <strong>{formatAUD(subtotalExGst)}</strong>
                  </div>
                </>
              ) : invoiceType === 'GST_EXCLUDED' ? (
                <>
                  <div>
                    Subtotal: <strong>{formatAUD(subtotalExGst)}</strong>
                  </div>
                  <div>
                    GST excluded: <strong>{formatAUD(0)}</strong>
                  </div>
                  <div>
                    Grand total: <strong>{formatAUD(grandTotal)}</strong>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    Subtotal (ex GST):{' '}
                    <strong>{formatAUD(subtotalExGst)}</strong>
                  </div>
                  <div>
                    GST (10%): <strong>{formatAUD(gst)}</strong>
                  </div>
                  <div>
                    Grand total ({grandTotalLabel(invoiceType)}):{' '}
                    <strong>{formatAUD(grandTotal)}</strong>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="eim-footer">
          <button
            type="button"
            className="eim-btn eim-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          {canFinalize && (
            <button
              type="button"
              className="eim-btn eim-btn--primary"
              disabled={finalizing || saving}
              onClick={handleFinalize}
            >
              {finalizing ? 'Finalizing…' : 'Save & Finalize'}
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              className="eim-btn eim-btn--primary"
              disabled={saving || finalizing}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
